import { type Reviewer } from "components/Submission/ReviewersEditList";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  ReviewerStatus,
  ReviewMode,
  SignatureType,
  SubmissionReviewerInput,
} from "generated-graphql/graphql";
import { type SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { DateTime } from "luxon";
import { parseDateLikeToLocalDateTime, toUtcMidnightIsoFromDateLike } from "util/dates";

type PersistedReviewer = {
  id: string;
  isStaff: boolean;
  isSigner: boolean;
  reviewOrder: number;
  dueDate?: string | null;
  reviewerStatus?: ReviewerStatus | null;
  signerNameOverride?: string | null;
  signerEmailOverride?: string | null;
  streetAddress1?: string | null;
  streetAddress2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  user: {
    id: string;
    email: string;
    person?: { first?: string | null; last?: string | null } | null;
  };
};

type OrderedConfigReviewer = {
  id: string;
  isStaff: boolean;
  isSigner: boolean;
  reviewOrder: number;
  dueDate?: DateTime;
  reviewerStatus?: ReviewerStatus | null;
  signerNameOverride?: string;
  signerEmailOverride?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

export function toUiReviewer(reviewer: PersistedReviewer): Reviewer {
  return {
    id: reviewer.user.id,
    submissionReviewerId: reviewer.id,
    name: reviewer.user.person ? getFullName(reviewer.user.person) : reviewer.user.email,
    email: reviewer.user.email,
    dueDate: reviewer.dueDate ? parseDateLikeToLocalDateTime(reviewer.dueDate) : undefined,
    isSigner: reviewer.isSigner,
    isStaff: reviewer.isStaff,
    order: reviewer.reviewOrder,
    reviewerStatus: reviewer.reviewerStatus ?? null,
  };
}

export function toUiSigner(reviewer: PersistedReviewer): SubmissionReviewerSigner {
  return {
    id: reviewer.user.id,
    submissionReviewerId: reviewer.id,
    name: reviewer.user.person ? getFullName(reviewer.user.person) : reviewer.user.email,
    email: reviewer.user.email,
    order: reviewer.reviewOrder,
    signerNameOverride: reviewer.signerNameOverride ?? undefined,
    signerEmailOverride: reviewer.signerEmailOverride ?? undefined,
    streetAddress1: reviewer.streetAddress1 ?? undefined,
    streetAddress2: reviewer.streetAddress2 ?? undefined,
    city: reviewer.city ?? undefined,
    state: reviewer.state ?? undefined,
    zip: reviewer.zip ?? undefined,
    country: reviewer.country ?? undefined,
    dueDate: reviewer.dueDate ? parseDateLikeToLocalDateTime(reviewer.dueDate) : undefined,
    isSigner: true,
    isStaff: reviewer.isStaff,
    reviewerStatus: reviewer.reviewerStatus ?? null,
  };
}

export function buildOrderedConfig(
  reviewers: Reviewer[],
  signer: SubmissionReviewerSigner | null
): OrderedConfigReviewer[] {
  const reviewerConfigs: OrderedConfigReviewer[] = reviewers.map((reviewer) => ({
    id: reviewer.id,
    isStaff: reviewer.isStaff,
    isSigner: false,
    reviewOrder: 0,
    dueDate: reviewer.dueDate,
    reviewerStatus: reviewer.reviewerStatus ?? null,
    streetAddress1: reviewer.streetAddress1,
    streetAddress2: reviewer.streetAddress2,
    city: reviewer.city,
    state: reviewer.state,
    zip: reviewer.zip,
    country: reviewer.country,
  }));
  const signerConfigs: OrderedConfigReviewer[] = signer
    ? [
        {
          id: signer.id,
          isStaff: signer.isStaff,
          isSigner: true,
          reviewOrder: 0,
          dueDate: signer.dueDate,
          reviewerStatus: signer.reviewerStatus ?? null,
          signerNameOverride: signer.signerNameOverride,
          signerEmailOverride: signer.signerEmailOverride,
          streetAddress1: signer.streetAddress1,
          streetAddress2: signer.streetAddress2,
          city: signer.city,
          state: signer.state,
          zip: signer.zip,
          country: signer.country,
        },
      ]
    : [];

  const combined = [...reviewerConfigs, ...signerConfigs];
  const staff = combined.filter((reviewer) => reviewer.isStaff);
  const nonStaff = combined.filter((reviewer) => !reviewer.isStaff);
  return [
    ...staff.map((reviewer) => ({ ...reviewer, reviewOrder: 0 })),
    ...nonStaff.map((reviewer, index) => ({ ...reviewer, reviewOrder: index + 1 })),
  ];
}

export function toReviewerInputs(
  orderedReviewers: OrderedConfigReviewer[]
): SubmissionReviewerInput[] {
  return orderedReviewers.map((reviewer) => ({
    userId: reviewer.id,
    reviewOrder: reviewer.reviewOrder,
    isStaff: reviewer.isStaff,
    isRequired: true,
    isSigner: reviewer.isSigner,
    dueDate: reviewer.dueDate
      ? (toUtcMidnightIsoFromDateLike(reviewer.dueDate) ?? undefined)
      : undefined,
    signerNameOverride: reviewer.signerNameOverride || null,
    signerEmailOverride: reviewer.signerEmailOverride || null,
    streetAddress1: reviewer.streetAddress1 || undefined,
    streetAddress2: reviewer.streetAddress2 || undefined,
    city: reviewer.city || undefined,
    state: reviewer.state || undefined,
    zip: reviewer.zip || undefined,
    country: reviewer.country || undefined,
    reviewerStatus: reviewer.reviewerStatus ?? undefined,
  }));
}

export function serializeConfig(config: {
  reviewers: Reviewer[];
  signer: SubmissionReviewerSigner | null;
  signatureType: SignatureType;
  reviewRequired: boolean;
  peerReviewRequired: boolean;
  reviewMode: ReviewMode;
}): string {
  return JSON.stringify({
    signatureType: config.signatureType,
    reviewRequired: config.reviewRequired,
    peerReviewRequired: config.peerReviewRequired,
    reviewMode: config.reviewMode,
    reviewers: toReviewerInputs(buildOrderedConfig(config.reviewers, config.signer)),
  });
}
